import { InfoCircleOutlined } from '@ant-design/icons';
import { Tooltip } from 'antd';
import { APPLICATION_CONFIRM_RESULT, getMoment } from 'constants/GlobalConfig';
import { ProgressStatusTag } from '../common/ProgressStatusTag';
import { ScoringStatusTag } from '../common/ScoringStatusTag';
import { ColumnsType } from 'antd/lib/table/Table';

// antd の Tree data の表示を使うための型
type DataSource = AppStatusModal.HistoryView['history'][number];
const moment = getMoment();

export const createColumns = (
    filterOptions: string[],
    application: App.ForAdmin | App.ForManager | undefined
): ColumnsType<DataSource> => {
    return [
        {
            title: '',
            width: 200,
            render: (_: any, record: any) => {
                return <div>{record.title}</div>;
            },
            onCell: (record: any, index: any) => {
                if (index === 1) {
                    return { rowSpan: record.rowSpan };
                }
                if (index > 1) {
                    return { rowSpan: 0 };
                }
                return {};
            },
        },
        {
            colSpan: 0,
            children: generateLineItem(application, filterOptions),
        },
    ];
};

const generateLineItem = (application: App.ForAdmin | App.ForManager | undefined, filterOptions: string[]) => {
    return (
        application?.line_items &&
        application.line_items.map((lineItem: App.ApplicationLineItem, index: number) => {
            return {
                title: lineItem.label,
                key: index,
                children: [
                    {
                        align: 'center',
                        title: (
                            <>
                                <span style={{ marginRight: '0.5em' }}>
                                    {APPLICATION_CONFIRM_RESULT.SCORE_GIVEN_AND_SCORE_MAXIMUM.TITLE}
                                </span>
                                <Tooltip title='複数回解答した場合、最新の得点・満点が表示されます'>
                                    <InfoCircleOutlined />
                                </Tooltip>
                            </>
                        ),
                        key: APPLICATION_CONFIRM_RESULT.SCORE_GIVEN_AND_SCORE_MAXIMUM.KEY,
                        hidden: filterOptions.some(
                            (item) => item === APPLICATION_CONFIRM_RESULT.SCORE_GIVEN_AND_SCORE_MAXIMUM.KEY
                        ),
                        render: (_: any, record: any) => {
                            if (!record?.take?.line_items?.[index]?.score_line_items) {
                                return <span>{`- / ${lineItem.score_maximum}`}</span>;
                            }

                            return (
                                <>
                                    <span>
                                        {`${
                                            record?.take?.line_items?.[index]?.score_line_items?.[0]?.score_given ?? '-'
                                        } / ${
                                            record?.take?.line_items?.[index]?.score_line_items?.[0]?.score_maximum ??
                                            '-'
                                        }`}
                                    </span>
                                </>
                            );
                        },
                    },
                    {
                        title: APPLICATION_CONFIRM_RESULT.ACTIVITY_PROGRESS.TITLE,
                        key: APPLICATION_CONFIRM_RESULT.ACTIVITY_PROGRESS.KEY,
                        hidden: filterOptions.some((item) => item === APPLICATION_CONFIRM_RESULT.ACTIVITY_PROGRESS.KEY),
                        render: (_: any, record: any) => (
                            <ProgressStatusTag
                                status={record?.take?.line_items?.[index]?.score_line_items?.[0]?.activity_progress}
                            />
                        ),
                    },
                    {
                        title: APPLICATION_CONFIRM_RESULT.GRADING_PROGRESS.TITLE,
                        key: APPLICATION_CONFIRM_RESULT.GRADING_PROGRESS.KEY,
                        hidden: filterOptions.some((item) => item === APPLICATION_CONFIRM_RESULT.GRADING_PROGRESS.KEY),
                        render: (_: any, record: any) => (
                            <ScoringStatusTag
                                status={record?.take?.line_items?.[index]?.score_line_items?.[0]?.grading_progress}
                            />
                        ),
                    },
                    {
                        title: APPLICATION_CONFIRM_RESULT.TIMESTAMP.TITLE,
                        key: APPLICATION_CONFIRM_RESULT.TIMESTAMP.KEY,
                        hidden: filterOptions.some((item) => item === APPLICATION_CONFIRM_RESULT.TIMESTAMP.KEY),
                        render: (_: any, record: any) => {
                            const updatedAt = record?.take?.line_items?.[index]?.score_line_items?.[0]?.updated_at;
                            const createdAt = record?.take?.line_items?.[index]?.score_line_items?.[0]?.created_at;

                            if (updatedAt || createdAt) {
                                return <span>{moment.unix(updatedAt ?? createdAt).format('YYYY/MM/DD HH:mm')}</span>;
                            }
                            return <></>;
                        },
                    },
                    {
                        title: APPLICATION_CONFIRM_RESULT.COMMENT.TITLE,
                        key: APPLICATION_CONFIRM_RESULT.COMMENT.KEY,
                        hidden: filterOptions.some((item) => item === APPLICATION_CONFIRM_RESULT.COMMENT.KEY),
                        render: (_: any, record: any) => {
                            if (record?.take?.line_items?.[index]?.score_line_items?.[0]?.comment) {
                                return (
                                    <span
                                        style={{
                                            width: '20ch',
                                            wordBreak: 'break-all',
                                            display: 'inline-block',
                                            overflowWrap: 'break-word',
                                        }}>
                                        {record.take.line_items[index].score_line_items?.[0].comment}
                                    </span>
                                );
                            } else {
                                return '-';
                            }
                        },
                    },
                    {
                        title: APPLICATION_CONFIRM_RESULT.SCORING_USER_ID.TITLE,
                        key: APPLICATION_CONFIRM_RESULT.SCORING_USER_ID.KEY,
                        hidden: filterOptions.some((item) => item === APPLICATION_CONFIRM_RESULT.SCORING_USER_ID.KEY),
                        render: (_: any, record: any) => {
                            if (record?.take?.line_items?.[index]?.score_line_items?.[0]?.scoring_user_id) {
                                return (
                                    <span
                                        style={{
                                            width: '20ch',
                                            wordBreak: 'break-all',
                                            display: 'inline-block',
                                            overflowWrap: 'break-word',
                                        }}>
                                        {record.take.line_items[index].score_line_items?.[0].comment}
                                    </span>
                                );
                            } else {
                                return '-';
                            }
                        },
                    },
                    {
                        title: APPLICATION_CONFIRM_RESULT.STARTED_AT.TITLE,
                        key: APPLICATION_CONFIRM_RESULT.STARTED_AT.KEY,
                        hidden: filterOptions.some((item) => item === APPLICATION_CONFIRM_RESULT.STARTED_AT.KEY),
                        render: (_: any, record: any) => {
                            if (record?.take?.line_items?.[index]?.score_line_items?.[0]?.started_at) {
                                return (
                                    <span>
                                        {moment
                                            .unix(record.take.line_items[index].score_line_items[0].started_at)
                                            .format('YYYY/MM/DD HH:mm')}
                                    </span>
                                );
                            } else {
                                return '-';
                            }
                        },
                    },
                    {
                        title: APPLICATION_CONFIRM_RESULT.SUBMITTED_AT.TITLE,
                        key: APPLICATION_CONFIRM_RESULT.SUBMITTED_AT.KEY,
                        hidden: filterOptions.some((item) => item === APPLICATION_CONFIRM_RESULT.SUBMITTED_AT.KEY),
                        render: (_: any, record: any) => {
                            if (record?.take?.line_items?.[index]?.score_line_items?.[0]?.submitted_at) {
                                return (
                                    <span>
                                        {moment
                                            .unix(record.take.line_items[index].score_line_items[0].submitted_at)
                                            .format('YYYY/MM/DD HH:mm')}
                                    </span>
                                );
                            } else {
                                return '-';
                            }
                        },
                    },
                ].filter((item) => !item.hidden),
            };
        })
    );
};
